import apiClient from './client';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupCredentials {
  email: string;
  password: string;
  name: string;
}

export interface VerifyEmailParams {
  email: string;
  code: string;
}

export interface ForgotPasswordParams {
  email: string;
}

export interface ResetPasswordParams {
  email: string;
  code: string;
  new_password: string;
}

export interface AuthTokensResponse {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

export interface ChallengeResponse {
  challenge: string;
  session?: string;
  email: string;
}

export type LoginResponse = AuthTokensResponse | ChallengeResponse;

export interface InviteUserParams {
  email: string;
  name: string;
}

export interface RespondToChallengeParams {
  challenge: string;
  session: string;
  email: string;
  new_password: string;
}

export interface UpdateUserParams {
  is_admin?: boolean;
  name?: string;
}

export interface UserResponse {
  id: string;
  email: string;
  name?: string;
  status?: string;
  is_admin?: boolean;
}

export type AppUser = UserResponse;

export const authApi = {
  login: async (credentials: LoginCredentials) => {
    const response = await apiClient.post<{ data: LoginResponse }>('/auth/login', credentials);
    return response.data.data;
  },

  signup: async (credentials: SignupCredentials) => {
    const response = await apiClient.post<{ data: { message: string } }>('/auth/signup', credentials);
    return response.data.data;
  },

  verify: async (params: VerifyEmailParams) => {
    const response = await apiClient.post<{ data: { message: string } }>('/auth/verify', params);
    return response.data.data;
  },

  refresh: async (refreshToken: string) => {
    const response = await apiClient.post<{ data: AuthTokensResponse }>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data.data;
  },

  me: async () => {
    const response = await apiClient.get<{ data: UserResponse }>('/auth/me');
    return response.data.data;
  },

  forgotPassword: async (params: ForgotPasswordParams) => {
    const response = await apiClient.post<{ data: { message: string } }>('/auth/forgot-password', params);
    return response.data.data;
  },

  resetPassword: async (params: ResetPasswordParams) => {
    const response = await apiClient.post<{ data: { message: string } }>('/auth/reset-password', params);
    return response.data.data;
  },

  listUsers: async () => {
    const response = await apiClient.get<{ data: { users: AppUser[] } }>('/auth/users');
    return response.data.data.users;
  },

  createUser: async (params: InviteUserParams) => {
    const response = await apiClient.post<{ data: { message: string; user_id: string; email: string } }>(
      '/auth/users',
      params,
    );
    return response.data.data;
  },

  deleteUser: async (userId: string) => {
    const response = await apiClient.delete<{ data: { message: string } }>(`/auth/users/${userId}`);
    return response.data.data;
  },

  updateUser: async (userId: string, params: UpdateUserParams) => {
    const response = await apiClient.patch<{ data: { message: string } }>(`/auth/users/${userId}`, params);
    return response.data.data;
  },

  adminResetPassword: async (userId: string) => {
    const response = await apiClient.post<{ data: { message: string } }>(`/auth/users/${userId}/reset-password`);
    return response.data.data;
  },

  respondToChallenge: async (params: RespondToChallengeParams) => {
    const response = await apiClient.post<{ data: AuthTokensResponse }>('/auth/challenge', params);
    return response.data.data;
  },
};
